import { useState, useEffect, useRef, useMemo } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import { HTTPMethod, callApi as OrderRequest } from "../../../services/HttpService/HttpService";
import { PatientsApi, ReportsApi } from "../../../constants/ApiConstants";
import { DEFAULT_FILTERS } from "../../../constants/PatientSurveyReportConstants";

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const useSurveyReportData = () => {

    const didInitRef = useRef(false);
    const accordionRef = useRef(null);

    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const [isGrid, setGridView] = useState(true);
    const [optionsLocation, setOptionsLocation] = useState([]);

    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState("loading");

    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [validDate, setValidDate] = useState(false);

    const [detailsMode, setDetailsMode] = useState("high");
    const [detailsPage, setDetailsPage] = useState(0);
    const [detailsPageSize, setDetailsPageSize] = useState(25);
    const [detailsTotal, setDetailsTotal] = useState(0);
    const [detailsRows, setDetailsRows] = useState([]);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

    const [activeTimeframe, setActiveTimeframe] = useState({
        timePeriod: DEFAULT_FILTERS.timePeriod,
        month: DEFAULT_FILTERS.month,
    });

    const { periodStart, periodEnd } = useMemo(() => {
        const now = dayjs();
        let start = null;
        let end = now.endOf("day");

        if (activeTimeframe.timePeriod === "Monthly" && activeTimeframe.month) {
            let m;

            if (dayjs.isDayjs(activeTimeframe.month)) m = activeTimeframe.month;
            else if (activeTimeframe.month instanceof Date && !isNaN(activeTimeframe.month.valueOf())) m = dayjs(activeTimeframe.month);
            else if (typeof activeTimeframe.month === "string") {
                m = dayjs(activeTimeframe.month,
                    ["YYYY-MM", "YYYY-MM-DD", "MM/YYYY", "MMMM YYYY", "MMM YYYY"],
                    true
                );
            }

            if (m?.isValid()) {
                start = m.startOf("month");
                end = m.endOf("month");
            }

        } else if (activeTimeframe.timePeriod === "Past 3 months") {
            start = now.startOf("month").subtract(3, "month");
            end = now.startOf("month");

        } else if (activeTimeframe.timePeriod === "Past 6 months") {
            start = now.startOf("month").subtract(6, "month");
            end = now.startOf("month");

        } else if (activeTimeframe.timePeriod === "Year to date") {
            start = dayjs(new Date(now.year(), 0, 1)).startOf("day");
            end = now.startOf("month");
        }

        return {
            periodStart: start ? start.toISOString() : null,
            periodEnd: end ? end.toISOString() : null,
        };
    }, [activeTimeframe.timePeriod, activeTimeframe.month]);

    const hasMetricsForPeriod = useMemo(() => {
        const arr = reportData?.metricsOverTime ?? [];
        if (!arr.length || !periodStart || !periodEnd) return false;

        const start = dayjs(periodStart);
        const end = dayjs(periodEnd);

        return arr.some(d => {
            const t = dayjs(d.reportDate);
            const inRange =
                (t.isAfter(start) || t.isSame(start)) &&
                (t.isBefore(end) || t.isSame(end));

            const hasValues =
                d?.averageRating != null ||
                d?.satisfactionRate != null ||
                d?.totalResponses != null ||
                d?.issuesReported != null;

            return inRange && hasValues;
        });
    }, [reportData?.metricsOverTime, periodStart, periodEnd]);

    const handleSearchFilterHospiceBranch = async () => {
        const response = await OrderRequest(
            HTTPMethod.Get,
            PatientsApi.patientsHospiceBranchSearch + "?LocationName="
        );
        if (response && response.status_code === 200 && response.data) {
            setOptionsLocation(response.data.location_info || []);
        } else {
            setOptionsLocation([]);
        }
    };

    const buildReportRequestBody = (overrideFilters) => {
        const now = new Date();
        const f = overrideFilters ?? appliedFilters;

        let start_date = null;
        let end_date = now.toISOString();

        if (f.timePeriod === "Monthly" && f.month) {
            let m;

            if (dayjs.isDayjs(f.month)) m = f.month;
            else if (f.month instanceof Date && !isNaN(f.month.valueOf())) m = dayjs(f.month);
            else if (typeof f.month === "string") {
                m = dayjs(f.month,
                    ["YYYY-MM", "YYYY-MM-DD", "MM/YYYY", "MMMM YYYY", "MMM YYYY"],
                    true
                );
            }

            if (m?.isValid()) {
                start_date = m.startOf("month").toDate().toISOString();
                end_date = m.endOf("month").toDate().toISOString();
            }

        } else if (f.timePeriod === "Past 3 months") {
            start_date = dayjs().startOf("month").subtract(3, "month").toDate().toISOString();
            end_date = dayjs().startOf("month").toDate().toISOString();

        } else if (f.timePeriod === "Past 6 months") {
            start_date = dayjs().startOf("month").subtract(6, "month").toDate().toISOString();
            end_date = dayjs().startOf("month").toDate().toISOString();

        } else if (f.timePeriod === "Year to date") {
            start_date = dayjs(new Date(now.getFullYear(), 0, 1)).startOf("day").toDate().toISOString();
            end_date = dayjs().startOf("month").toDate().toISOString();
        }

        const location_ids = Array.isArray(f.branch)
            ? f.branch.map(b => Number(b.location_id)).filter(Boolean)
            : [];

        const order_type =
            f.orderType && f.orderType !== "All" ? f.orderType : "All";

        return {
            start_date,
            end_date,
            location_ids,
            order_type,
            survey_version: 2,
        };
    };

    const fetchSurveyReport = async (overrideFilters) => {
        try {
            setLoading(true);
            setView("loading");

            const now = new Date();

            let start_date = null;
            let end_date = now.toISOString();

            const f = overrideFilters ?? appliedFilters;

            if (f.timePeriod === "Monthly" && f.month) {
                let m;

                if (dayjs.isDayjs(f.month)) m = f.month;
                else if (f.month instanceof Date && !isNaN(f.month.valueOf())) m = dayjs(f.month);
                else if (typeof f.month === "string") {
                    m = dayjs(
                        f.month,
                        ["YYYY-MM", "YYYY-MM-DD", "MM/YYYY", "MMMM YYYY", "MMM YYYY"],
                        true
                    );
                }

                if (!m || !m.isValid()) {
                    setLoading(false);
                    setView("empty");
                    return;
                }

                start_date = m.startOf("month").toDate().toISOString();
                end_date = m.endOf("month").toDate().toISOString();

            } else if (f.timePeriod === "Past 3 months") {
                start_date = dayjs().startOf("month").subtract(3, "month").toDate().toISOString();
                end_date = dayjs().startOf("month").toDate().toISOString();

            } else if (f.timePeriod === "Past 6 months") {
                start_date = dayjs().startOf("month").subtract(6, "month").toDate().toISOString();
                end_date = dayjs().startOf("month").toDate().toISOString();

            } else if (f.timePeriod === "Year to date") {
                start_date = dayjs(new Date(now.getFullYear(), 0, 1)).startOf("day").toDate().toISOString();
                end_date = dayjs().startOf("month").toDate().toISOString();
            }

            const location_ids = Array.isArray(f.branch)
                ? f.branch.map(b => Number(b.location_id)).filter(Boolean)
                : [];

            const order_type =
                f.orderType && f.orderType !== "All" ? f.orderType : "All";

            const requestBody = {
                start_date,
                end_date,
                location_ids,
                order_type,
                survey_version: 2,
            };

            const response = await OrderRequest(
                HTTPMethod.Post,
                ReportsApi.patientSurveyReport,
                requestBody
            );

            if (response?.status_code === 200 && response.data?.report) {

                const api = response.data.report;

                const normalized = {
                    summary: {
                        averageRating: api.summary?.average_rating ?? null,
                        averageRatingPrev: api.summary?.average_rating_prev ?? null,
                        averageRatingTrend: api.summary?.average_rating_trend ?? null,

                        totalResponses: api.summary?.total_responses ?? 0,
                        totalResponsesPrev: api.summary?.total_responses_prev ?? 0,
                        totalResponsesTrend: api.summary?.total_responses_trend ?? null,

                        satisfactionRate: api.summary?.satisfaction_rate ?? 0,
                        satisfactionRatePrev: api.summary?.satisfaction_rate_prev ?? 0,
                        satisfactionRateTrend: api.summary?.satisfaction_rate_trend ?? null,

                        issuesReported: api.summary?.issues_reported ?? 0,
                        issuesReportedPrev: api.summary?.issues_reported_prev ?? 0,
                        issuesReportedTrend: api.summary?.issues_reported_trend ?? null,
                    },

                    metricsOverTime: (api.metrics_over_time ?? []).map(it => ({
                        reportDate: it.report_date,
                        averageRating: it.average_rating,
                        satisfactionRate: it.satisfaction_rate,
                        totalResponses: it.total_responses,
                        issuesReported: it.issues_reported,
                    })),

                    ratingDistribution: (api.rating_distribution ?? []).map(it => ({
                        rating: it.rating,
                        count: it.count,
                    })),

                    questionBreakdown: (api.question_breakdown ?? []).map(q => ({
                        feedbackQuestionId: q.feedback_question_id,
                        questionText: q.question_text,
                        yesCount: q.yes_count,
                        noCount: q.no_count,
                        yesPct: q.yes_pct,
                        noPct: q.no_pct,
                    })),

                    issuesBreakdown: {
                        categories: (api.issues_breakdown ?? []).map(c => ({
                            feedbackQuestionId: c.feedback_question_id,
                            category: c.category,
                            count: c.count,
                        })),
                        topIssues: (api.top_issues ?? []).map(t => ({
                            issueText: t.issue_text,
                            occurrences: t.occurrences,
                        })),
                    },

                    feedbackHighlights: (api.feedback_highlights ?? []).map(h => ({
                        comment: h.comment,
                        rating: h.rating,
                        orderType: h.order_type,
                        completedOn: h.completed_on,
                        respondent: (h.respondent ?? "").trim()
                    })),
                };

                setReportData(normalized);

                const hasAny =
                    (normalized.summary?.totalResponses ?? 0) > 0 ||
                    (normalized.metricsOverTime?.length ?? 0) > 0 ||
                    (normalized.ratingDistribution?.some(x => Number(x?.count) > 0)) ||
                    (normalized.questionBreakdown?.length ?? 0) > 0;

                setView(hasAny ? "ready" : "empty");

            } else {
                setReportData(null);
                setView("empty");
            }

        } catch (error) {
            console.error("Failed to fetch survey report:", error);
            setReportData(null);
            setView("error");
        } finally {
            setLoading(false);
        }
    };

    const fetchSurveyDetails = async (overrideFilters) => {
        try {
            setDetailsLoading(true);

            const body = buildReportRequestBody(overrideFilters);
            body.details_mode = detailsMode === "high" ? "High" : "Low";
            body.limit = detailsPageSize;
            body.offset = detailsPage + 1;

            const response = await OrderRequest(
                HTTPMethod.Post,
                ReportsApi.patientSurveyReport,
                body
            );

            if (response?.status_code === 200) {
                const details = response?.data?.report?.details;
                setDetailsRows(details?.rows ?? []);
                setDetailsTotal(details?.total ?? 0);
            } else {
                setDetailsRows([]);
                setDetailsTotal(0);
            }

        } catch (e) {
            console.error("fetchSurveyDetails error", e);
            setDetailsRows([]);
            setDetailsTotal(0);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleChangePatientsFilter = (date) => {
        setSelectedDate(date);
        const isoMonth = dayjs(date).format("YYYY-MM");
        setFilters(prev => ({ ...prev, month: isoMonth }));
    };
    
    const formatBranchChip = (branch) => {
        if (Array.isArray(branch)) {
            return branch
                .map(b => (typeof b === "object" ? b.location_name : String(b)))
                .join(", ");
        }
        if (branch && typeof branch === "object") {
            return branch.location_name ?? "";
        }
        return branch ?? "";
    };

    useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;

        (async () => {
            setView("loading");
            await handleSearchFilterHospiceBranch();
            setAppliedFilters(DEFAULT_FILTERS);
            await fetchSurveyReport(DEFAULT_FILTERS);
        })();
    }, []);

    useEffect(() => {
        if (!isGrid) {
            fetchSurveyDetails(appliedFilters);
        }
        // eslint-disable-next-line
    }, [
        isGrid,
        detailsMode,
        detailsPage,
        detailsPageSize,
        appliedFilters.timePeriod,
        appliedFilters.month,
        appliedFilters.orderType,
        JSON.stringify(appliedFilters.branch)
    ]);

    const categories = reportData?.issuesBreakdown?.categories || [];
    const topIssues = reportData?.issuesBreakdown?.topIssues || [];

    const hasCategoryWithCount = categories.some(
        c => (c.count ?? c.issueCount ?? 0) > 0
    );
    const hasTopIssue = topIssues.length > 0;
    const hasIssues = hasCategoryWithCount || hasTopIssue;

    const hasFeedbackHighlights = (reportData?.feedbackHighlights ?? []).length > 0;

    const averageRatingTrend = reportData?.summary?.averageRatingTrend;
    const totalResponsesTrend = reportData?.summary?.totalResponsesTrend;
    const satisfactionRateTrend = reportData?.summary?.satisfactionRateTrend;
    const issuesReportedTrend = reportData?.summary?.issuesReportedTrend;

    return {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        activeTimeframe,
        setActiveTimeframe,
        optionsLocation,
        selectedDate,
        setSelectedDate,
        validDate,
        setValidDate,
        isAccordionOpen,
        setIsAccordionOpen,
        isGrid,
        setGridView,
        reportData,
        loading,
        view,
        detailsMode,
        setDetailsMode,
        detailsPage,
        setDetailsPage,
        detailsPageSize,
        setDetailsPageSize,
        detailsTotal,
        detailsRows,
        detailsLoading,
        periodStart,
        periodEnd,
        hasMetricsForPeriod,
        categories,
        topIssues,
        hasIssues,
        hasFeedbackHighlights,
        averageRatingTrend,
        totalResponsesTrend,
        satisfactionRateTrend,
        issuesReportedTrend,
        handleSearchFilterHospiceBranch,
        fetchSurveyReport,
        fetchSurveyDetails,
        handleChangePatientsFilter,
        formatBranchChip,
        accordionRef
    };
};

export default useSurveyReportData;
